import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import Cookies from 'js-cookie';
import { compact } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, DeleteItemModal, Dropdown, Input, Textarea } from '../../../../components/common';
import TagsInput from '../../../../components/common/TagsInput';
import useModal from '../../../../components/partials/Modal/useModal';
import { useDeleteCampaign, useGetCities, useGetLoanOfficers, useGetStates } from '../../../../service/hooks';
import { CAMPAIGN_TYPE_ACTIONS, DAYS, MLS_SOURCE, MODAL_TYPES, OWNERSHIP, STANDARD_PROPERTY_TYPES, TYPE_CUSTOM_AUDIENCE, V2_PROPERTY_TYPES, V2_STANDARD_STATUSES } from '../../../../utils/constants';
import { cn } from '../../../../utils/helpers';
import { DownloadCampaignModal, StartCampaignModal } from '../components';
import { CAMPAIGN_FORM_VALUES } from './schema';

const DAYS_OPTIONS = Object.keys(DAYS).map((day) => ({
  label: day,
  value: DAYS[day as keyof typeof DAYS],
}));

const BOOLEAN_OPTIONS = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

const TIME_OPTIONS = [
  { label: '12am', value: 0 },
  { label: '1am', value: 1 },
  { label: '2am', value: 2 },
  { label: '3am', value: 3 },
  { label: '4am', value: 4 },
  { label: '5am', value: 5 },
  { label: '6am', value: 6 },
  { label: '7am', value: 7 },
  { label: '8am', value: 8 },
  { label: '9am', value: 9 },
  { label: '10am', value: 10 },
  { label: '11am', value: 11 },
  { label: '12pm', value: 12 },
  { label: '1pm', value: 13 },
  { label: '2pm', value: 14 },
  { label: '3pm', value: 15 },
  { label: '4pm', value: 16 },
  { label: '5pm', value: 17 },
  { label: '6pm', value: 18 },
  { label: '7pm', value: 19 },
  { label: '8pm', value: 20 },
  { label: '9pm', value: 21 },
  { label: '10pm', value: 22 },
  { label: '11pm', value: 23 },
];

const STATUS_OPTIONS = [
  {
    label: 'Active',
    value: 'Active',
  },
  {
    label: 'Pending',
    value: 'Pending',
  },
  {
    label: 'Sold',
    value: 'Sold',
  },
  {
    label: 'Withdrawn',
    value: 'Withdrawn',
  },
];

const MLS_SOURCE_OPTIONS = [
  { label: 'V2 (tech6group)', value: MLS_SOURCE.V2 },
  { label: 'V1 (legacy)', value: MLS_SOURCE.V1 },
];

// V2 campaigns filter on the native RESO vocabulary directly (no V1 mapping).
const V2_STATUS_OPTIONS = V2_STANDARD_STATUSES.map((s) => ({ label: s, value: s }));
const V2_PROPERTY_TYPE_OPTIONS = V2_PROPERTY_TYPES.map((s) => ({ label: s, value: s }));

const TYPE_OPTIONS = [
  {
    label: 'Agent Import',
    value: 'AGENT_IMPORT'
  },
  {
    label: 'Custom Audience',
    value: 'CUSTOM_AUDIENCE'
  }
];

const OWNERSHIP_OPTIONS = Object.keys(OWNERSHIP).map((key) => ({
  label: OWNERSHIP[key as keyof typeof OWNERSHIP],
  value: OWNERSHIP[key as keyof typeof OWNERSHIP],
}));

const STANDARD_PROPERTY_TYPES_OPTIONS = Object.keys(STANDARD_PROPERTY_TYPES).map((key) => ({
  label: STANDARD_PROPERTY_TYPES[key as keyof typeof STANDARD_PROPERTY_TYPES],
  value: STANDARD_PROPERTY_TYPES[key as keyof typeof STANDARD_PROPERTY_TYPES],
}));

interface Props {
  isEdit?: boolean;
}

const CampaignForm: FC<Props> = ({ isEdit = true }) => {
  const { showModal } = useModal();
  const params = useParams();
  const { data: states } = useGetStates();
  const navigate = useNavigate();
  const { values, handleSubmit, isSubmitting, setFieldValue, handleChange, errors } =
    useFormikContext<CAMPAIGN_FORM_VALUES>();

  const isV2 = values.mlsSource === MLS_SOURCE.V2;

  const { mutate: deleteCampaign, isPending: deleteLoading } = useDeleteCampaign();
  const { data: loanOfficers, isLoading: loanOfficersLoading } = useGetLoanOfficers();
  const { data: cities } = useGetCities(values.stateOrProvince);

  const loanOfficerOptions = useMemo(
    () =>
      loanOfficers?.map((officer) => ({ label: `${officer.firstName} ${officer.lastName}`, value: officer.id })) ?? [],
    [loanOfficers],
  );

  const stateOptions = useMemo(
    () =>
      (states?.map((state) => ({ label: state.name, value: state.code })) ?? []).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    [states],
  );

  const cityOptions = useMemo(
    () => cities?.map((city) => ({ label: city.cityName, value: city.cityName })) ?? [],
    [cities],
  );

  const handleDownloadCampaign = (id: string) => {
    window.open(
      `${process.env.REACT_APP_API_END_POINT}api/v1/campaigns/${id}/preview?access_token=${Cookies.get('_token')}`,
      '_blank',
    );
  };

  const handleOpenStartCampaign = useCallback(
    (id: string) => {
      showModal({
        title: "RUN A CAMPAIGN",
        content: <StartCampaignModal id={id} />,
        disableOutsideClick: true,
        type: MODAL_TYPES.INFO,
      });
    },
    [showModal],
  );

  const handleOpenDownloadCampaign = useCallback(
    (id: string) => {
      showModal({
        title: 'DOWNLOAD CAMPAIGN',
        content: <DownloadCampaignModal onClickDownload={() => handleDownloadCampaign(id)} />,
        disableOutsideClick: true,
        type: MODAL_TYPES.INFO,
      });
    },
    [showModal],
  );

  const handleOpenDeleteModal = useCallback(
    (id: string) => {
      showModal({
        title: 'DELETE CAMPAIGN',
        content: (
          <DeleteItemModal
            text="campaign"
            onClickDelete={() =>
              deleteCampaign(id, {
                onSuccess: () => {
                  navigate('/');
                },
              })
            }
            isLoading={deleteLoading}
          />
        ),
        disableOutsideClick: true,
        type: MODAL_TYPES.WARNING,
      });
    },
    [deleteCampaign, deleteLoading, navigate, showModal],
  );

  return (
    <form onSubmit={handleSubmit} className={cn('max-w-[1280px] mx-auto', 'border border-[#B7B7B7]', 'rounded-lg')}>
      {!isEdit ? (
        <div className={cn('flex items-center justify-between', 'py-[22px] px-8 pt-4', 'border-b border-[#B7B7B7]')}>
          <div className="flex items-center gap-x-4">
            <Button onClick={() => navigate('/')} size="sm" variant="secondary" disabled={isSubmitting}>
              Back
            </Button>
            <Button
              onClick={() => handleOpenDeleteModal(params?.id as string)}
              size="sm"
              className="border-negaitve text-negative"
              variant="secondary"
              disabled={isSubmitting}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                <path
                  d="M3.85 0H10.15V2.18182H14V3.63636H12.5797L12.2297 16H1.7703L1.4203 3.63636H0V2.18182H3.85V0ZM5.25 2.18182H8.75V1.45455H5.25V2.18182ZM2.821 3.63636L3.1297 14.5455H10.8703L11.179 3.63636H2.821ZM5.0204 6.00509L7 8.06255L8.9796 6.00509L9.9701 7.03418L7.9898 9.09091L9.9701 11.1476L8.9796 12.1767L7 10.1193L5.0204 12.1767L4.0299 11.1476L6.0102 9.09091L4.0299 7.03418L5.0204 6.00509Z"
                  fill="#E94E48"
                />
              </svg>
              Delete Campaign
            </Button>
          </div>
          <div className="flex items-center gap-x-4">
            <Button
              onClick={() => handleOpenDownloadCampaign(params?.id as string)}
              size="sm"
              className="border-positive"
              variant="secondary"
              disabled={isSubmitting}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                <path
                  d="M7 12L2.625 7L3.85 5.55L6.125 8.15V0H7.875V8.15L10.15 5.55L11.375 7L7 12ZM1.75 16C1.26875 16 0.856626 15.804 0.513626 15.412C0.170626 15.02 -0.000581848 14.5493 1.48557e-06 14V11H1.75V14H12.25V11H14V14C14 14.55 13.8285 15.021 13.4855 15.413C13.1425 15.805 12.7307 16.0007 12.25 16H1.75Z"
                  fill="#2F2F2F"
                />
              </svg>
              Download
            </Button>
            <Button
              onClick={() => navigate('edit')}
              size="sm"
              className="border-positive"
              variant="secondary"
              disabled={isSubmitting}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                <path
                  d="M13.1644 4.08184C13.4951 3.77325 13.6771 3.36344 13.6771 2.9275C13.6771 2.49156 13.4951 2.08174 13.1644 1.77315L11.7766 0.478392C11.4459 0.169805 11.0066 0 10.5394 0C10.0721 0 9.63288 0.169805 9.303 0.477575L0 9.13026V12.7345H3.86138L13.1644 4.08184ZM10.5394 1.63274L11.928 2.92668L10.5367 4.21981L9.149 2.92586L10.5394 1.63274ZM1.75 11.1018V9.80785L7.91 4.07858L9.29775 5.37334L3.13862 11.1018H1.75ZM0 14.3673H14V16H0V14.3673Z"
                  fill="#2F2F2F"
                />
              </svg>
              Edit
            </Button>
            <Button size="sm" onClick={() => handleOpenStartCampaign(params?.id as string)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="16" viewBox="0 0 13 16" fill="none">
                <path
                  d="M13 7.99999C13.0005 8.20063 12.949 8.39797 12.8507 8.57284C12.7523 8.7477 12.6104 8.89413 12.4386 8.99789L1.79636 15.5082C1.61694 15.6181 1.41144 15.6781 1.20108 15.682C0.990724 15.6859 0.783142 15.6335 0.599773 15.5304C0.418149 15.4288 0.266852 15.2807 0.161439 15.1013C0.0560266 14.9219 0.000303474 14.7177 0 14.5096V1.49039C0.000303474 1.28231 0.0560266 1.07806 0.161439 0.898652C0.266852 0.719243 0.418149 0.571149 0.599773 0.469599C0.783142 0.366448 0.990724 0.314117 1.20108 0.318013C1.41144 0.321908 1.61694 0.381888 1.79636 0.491758L12.4386 7.0021C12.6104 7.10586 12.7523 7.25229 12.8507 7.42715C12.949 7.60202 13.0005 7.79936 13 7.99999Z"
                  fill="#EAEAEA"
                />
              </svg>
              Run Campaign
            </Button>
          </div>
        </div>
      ) : (
        <div className={cn('flex gap-4 justify-end', 'py-[22px] px-8 pt-4', 'border-b border-[#B7B7B7]')}>
          <Button onClick={() => navigate(-1)} size="sm" variant="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="sm" type="submit" disabled={isSubmitting || !!Object.keys(errors).length}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M3 1.5C2.60218 1.5 2.22064 1.65804 1.93934 1.93934C1.65804 2.22064 1.5 2.60218 1.5 3V15C1.5 15.3978 1.65804 15.7794 1.93934 16.0607C2.22064 16.342 2.60218 16.5 3 16.5V11.25C3 10.6533 3.23705 10.081 3.65901 9.65901C4.08097 9.23705 4.65326 9 5.25 9H12.75C13.3467 9 13.919 9.23705 14.341 9.65901C14.7629 10.081 15 10.6533 15 11.25V16.5C15.3978 16.5 15.7794 16.342 16.0607 16.0607C16.342 15.7794 16.5 15.3978 16.5 15V5.4315C16.4999 5.03371 16.3418 4.65224 16.0605 4.371L13.629 1.9395C13.3478 1.65818 12.9663 1.50008 12.5685 1.5H12V3.75C12 4.34674 11.7629 4.91903 11.341 5.34099C10.919 5.76295 10.3467 6 9.75 6H6.75C6.15326 6 5.58097 5.76295 5.15901 5.34099C4.73705 4.91903 4.5 4.34674 4.5 3.75V1.5H3ZM6 1.5V3.75C6 3.94891 6.07902 4.13968 6.21967 4.28033C6.36032 4.42098 6.55109 4.5 6.75 4.5H9.75C9.94891 4.5 10.1397 4.42098 10.2803 4.28033C10.421 4.13968 10.5 3.94891 10.5 3.75V1.5H6ZM13.5 16.5V11.25C13.5 11.0511 13.421 10.8603 13.2803 10.7197C13.1397 10.579 12.9489 10.5 12.75 10.5H5.25C5.05109 10.5 4.86032 10.579 4.71967 10.7197C4.57902 10.8603 4.5 11.0511 4.5 11.25V16.5H13.5ZM0 3C0 2.20435 0.31607 1.44129 0.87868 0.87868C1.44129 0.31607 2.20435 0 3 0H12.5685C13.3641 0.000169912 14.127 0.316352 14.6895 0.879L17.121 3.3105C17.6836 3.87298 17.9998 4.63592 18 5.4315V15C18 15.7956 17.6839 16.5587 17.1213 17.1213C16.5587 17.6839 15.7956 18 15 18H3C2.20435 18 1.44129 17.6839 0.87868 17.1213C0.31607 16.5587 0 15.7956 0 15V3Z"
                fill="#FFFFFF"
              />
            </svg>
            Save
          </Button>
        </div>
      )}
      <div className="flex flex-wrap gap-8 p-8">
        <div className={cn('border border-light-gray', 'rounded-lg', 'w-[450px]')}>
          <h5 className={cn('text-primary text-lg font-medium', 'px-8 py-4', 'border-b border-light-gray')}>
            Campaign Details
          </h5>
          <div className="grid grid-cols-2 p-8 gap-4">
            <Dropdown
              label="Type"
              value={values.type}
              options={TYPE_OPTIONS}
              onChange={(value) => {
                values.typeActions = [];
                setFieldValue('type', value);
              }}
              disabled={!isEdit}
              error={errors?.type}
            />
            {
              values.type === TYPE_CUSTOM_AUDIENCE &&
              <div className='col-span-2 mb-2'>
                <Input
                  label='Create Custom Audience'
                  value={CAMPAIGN_TYPE_ACTIONS.CREATE_CUSTOM_AUDIENCE}
                  onChange={handleChange}
                  name='typeActions'
                  type='checkbox'
                  disabled={!isEdit}
                  checked={values.typeActions.includes(CAMPAIGN_TYPE_ACTIONS.CREATE_CUSTOM_AUDIENCE)}
                  inputClassname='relative bottom-2'
                />
                <Input
                  label='Create Leads in Lofty'
                  value={CAMPAIGN_TYPE_ACTIONS.CREATE_LOFTY_LEADS}
                  onChange={(e) => {
                    if(values.typeActions.includes(CAMPAIGN_TYPE_ACTIONS.CREATE_LOFTY_LEADS)) {
                      setFieldValue('typeActions', 
                        values.typeActions.filter(action => action !== CAMPAIGN_TYPE_ACTIONS.CREATE_LOFTY_LEADS &&
                          action !== CAMPAIGN_TYPE_ACTIONS.ONLY_IMPORT_SELLERS_WITH_EMAIL)
                      )
                    } else {
                      setFieldValue('typeActions', [...values.typeActions, CAMPAIGN_TYPE_ACTIONS.CREATE_LOFTY_LEADS])
                    }
                  }}
                  name='typeActions'
                  type='checkbox'
                  disabled={!isEdit}
                  error={errors?.typeActions as string}
                  checked={values.typeActions.includes(CAMPAIGN_TYPE_ACTIONS.CREATE_LOFTY_LEADS)}
                  inputClassname='relative bottom-2'
                />
                {
                  values.typeActions.includes(CAMPAIGN_TYPE_ACTIONS.CREATE_LOFTY_LEADS) && 
                  <Input
                    label="Only import sellers with an email"
                    value={CAMPAIGN_TYPE_ACTIONS.ONLY_IMPORT_SELLERS_WITH_EMAIL}
                    onChange={handleChange}
                    name='typeActions'
                    type='checkbox'
                    disabled={!isEdit} 
                    checked={
                      [CAMPAIGN_TYPE_ACTIONS.CREATE_LOFTY_LEADS, CAMPAIGN_TYPE_ACTIONS.ONLY_IMPORT_SELLERS_WITH_EMAIL]
                        .every(action => values.typeActions.includes(action))
                    }
                    className=''
                    inputClassname='relative bottom-2 left-9'
                  /> 
                }
              </div>
            }
            <Input
              className="col-span-2"
              label="Campaign Title"
              value={values.name}
              onChange={handleChange}
              name="name"
              disabled={!isEdit}
              error={errors?.name}
            />
            <Dropdown
              className="col-span-2"
              label="Loan Officer ID"
              value={values.loanOfficerIds ?? []}
              options={loanOfficerOptions}
              onChange={(value) => {
                let loanOfficerIds = [...(values.loanOfficerIds ?? [])];

                if (loanOfficerIds.includes(value as string)) {
                  loanOfficerIds = loanOfficerIds.filter((day) => day !== value);
                } else {
                  loanOfficerIds.push(value as string);
                }

                setFieldValue('loanOfficerIds', compact(loanOfficerIds));
              }}
              disabled={!isEdit || !loanOfficerOptions.length || loanOfficersLoading}
              error={errors?.loanOfficerIds as string}
              isMultiSelect
            />
            <div className="grid grid-cols-2 col-span-2 gap-x-4">
              <p className="mb-2 col-span-2 text-sm">Repeat Every</p>
              <Dropdown
                label="Day"
                options={DAYS_OPTIONS}
                value={values.repeatDaysOfWeek ?? []}
                disabled={!isEdit}
                onChange={(value) => {
                  let repeatDaysOfWeek = [...(values.repeatDaysOfWeek ?? [])];

                  if (repeatDaysOfWeek.includes(value as DAYS)) {
                    repeatDaysOfWeek = repeatDaysOfWeek.filter((day) => day !== value);
                  } else {
                    repeatDaysOfWeek.push(value as DAYS);
                  }

                  setFieldValue('repeatDaysOfWeek', compact(repeatDaysOfWeek));
                }}
                isMultiSelect
              />
              <Dropdown
                label="Time"
                options={TIME_OPTIONS}
                value={values.hourOfDay}
                onChange={(value) => setFieldValue('hourOfDay', value)}
                disabled={!isEdit}
                error={errors?.hourOfDay}
              />
            </div>
            <Input
              className="col-span-2"
              label="Days Between Emails"
              value={values.daysBetweenEmails}
              onChange={handleChange}
              name="daysBetweenEmails"
              disabled={!isEdit}
              error={errors?.daysBetweenEmails}
              type="number"
            />
            <Textarea
              className="col-span-2"
              label="Notes"
              value={values.notes}
              onChange={handleChange}
              name="notes"
              disabled={!isEdit}
              rows={8}
            />
            <TagsInput
              onTagsChange={(tags) => setFieldValue('chimeTags', tags)}
              className="col-span-2"
              initialTags={values.chimeTags ?? []}
              disabled={!isEdit}
            />
          </div>
        </div>
        <div className={cn('border border-light-gray', 'rounded-lg', 'flex-1')}>
          <h5 className={cn('text-primary text-lg font-medium', 'px-8 py-4', 'border-b border-light-gray', 'h-fit')}>
            Campaign Search Query
          </h5>
          <div className="grid grid-cols-3 p-8 gap-4">
            <Dropdown
              className="col-span-3"
              label="MLS Source"
              value={values.mlsSource}
              options={MLS_SOURCE_OPTIONS}
              onChange={(value) => {
                setFieldValue('mlsSource', value);
                // Status and property-type vocabularies differ between V1 and V2 — reset them so a
                // value from the other source can't silently mismatch.
                setFieldValue('standardStatus', '');
                setFieldValue('standardPropertyTypes', []);
                if (value === MLS_SOURCE.V2) {
                  // Senior Community / New Construction don't exist in V2 and would zero out results.
                  setFieldValue('seniorCommunityYN', undefined);
                  setFieldValue('newConstructionYN', undefined);
                }
              }}
              disabled={!isEdit}
              error={errors?.mlsSource}
            />
            <Dropdown
              label="State"
              value={values.stateOrProvince}
              options={stateOptions}
              onChange={(value) => setFieldValue('stateOrProvince', value)}
              disabled={!isEdit}
              virtualized
              error={errors?.stateOrProvince}
            />
            <Dropdown
              label="City"
              value={values.city}
              options={cityOptions}
              onChange={(value) => setFieldValue('city', value)}
              disabled={!cityOptions.length || !isEdit}
              virtualized
              error={errors?.city}
            />
            <Dropdown
              label="Standard Status"
              value={values.standardStatus}
              options={isV2 ? V2_STATUS_OPTIONS : STATUS_OPTIONS}
              onChange={(value) => setFieldValue('standardStatus', value)}
              disabled={!isEdit}
              error={errors?.standardStatus}
            />
            <Input
              label="Radius in miles"
              type="number"
              value={values.radiusInMiles}
              onChange={handleChange}
              name="radiusInMiles"
              disabled={!isEdit}
            />
            <Input
              label="Subdivision name"
              value={values.subdivisionName}
              onChange={handleChange}
              name="subdivisionName"
              disabled={!isEdit}
            />
            <Input
              label="List office name"
              value={values.listOfficeName}
              onChange={handleChange}
              name="listOfficeName"
              disabled={!isEdit}
            />
          </div>
          <div className={cn('grid grid-cols-2 gap-4', 'p-8', 'border-y border-[#DADADA]')}>
            <Input
              label="Listing contract date from"
              value={values.listingContractDateFrom ? dayjs(values.listingContractDateFrom).format('YYYY-MM-DD') : ''}
              type="date"
              onChange={handleChange}
              name="listingContractDateFrom"
              disabled={!isEdit}
            />
            <Input
              label="Listing contract date to"
              value={values.listingContractDateTo ? dayjs(values.listingContractDateTo).format('YYYY-MM-DD') : ''}
              type="date"
              onChange={handleChange}
              name="listingContractDateTo"
              disabled={!isEdit}
            />
            <Input
              label="Listing contract date less than (days)"
              value={values.listingContractDateLessThanDays}
              type="number"
              onChange={handleChange}
              name="listingContractDateLessThanDays"
              disabled={!isEdit}
            />
            <Input
              label="Listing contract date greater than (days)"
              value={values.listingContractDateGreaterThanDays}
              type="number"
              onChange={handleChange}
              name="listingContractDateGreaterThanDays"
              disabled={!isEdit}
            />
          </div>
          <div className={cn('grid grid-cols-4 gap-4', 'p-8')}>
            <Input
              label="List price from"
              type="number"
              value={values.listPriceFrom}
              onChange={handleChange}
              name="listPriceFrom"
              disabled={!isEdit}
              min={0}
            />
            <Input
              label="List price to"
              type="number"
              value={values.listPriceTo}
              onChange={handleChange}
              name="listPriceTo"
              disabled={!isEdit}
            />
            <Input
              label="Year built from"
              value={values.yearBuiltFrom}
              onChange={handleChange}
              type="number"
              name="yearBuiltFrom"
              disabled={!isEdit}
              min={1900}
              max={2024}
            />
            <Input
              label="Year built to"
              value={values.yearBuiltTo}
              type="number"
              onChange={handleChange}
              name="yearBuiltTo"
              disabled={!isEdit}
              min={1900}
              max={2024}
            />
            <Dropdown
              label={isV2 ? 'Property type' : 'Standard property types'}
              options={isV2 ? V2_PROPERTY_TYPE_OPTIONS : STANDARD_PROPERTY_TYPES_OPTIONS}
              onChange={(value) => {
                let standardPropertyTypes = [...(values.standardPropertyTypes ?? [])];

                if(standardPropertyTypes.includes(value as STANDARD_PROPERTY_TYPES)) {
                  standardPropertyTypes = standardPropertyTypes.filter((propertyType) => propertyType !== value);
                } else {
                  standardPropertyTypes.push(value as STANDARD_PROPERTY_TYPES);
                }

                setFieldValue('standardPropertyTypes', compact(standardPropertyTypes));
              }}
              name="standardPropertyTypes"
              className="col-span-2"
              disabled={!isEdit}
              value={values.standardPropertyTypes ?? []}
              isMultiSelect
            />
            {/* SeniorCommunityYN / NewConstructionYN don't exist in V2 (0% of docs) — hide them. */}
            {!isV2 && (
              <Dropdown
                label="Senior Community"
                options={BOOLEAN_OPTIONS}
                onChange={(val) => setFieldValue('seniorCommunityYN', val)}
                name="seniorCommunityYN"
                className="col-span-2"
                disabled={!isEdit}
                value={values.seniorCommunityYN}
              />
            )}
            {!isV2 && (
              <Dropdown
                label="New construction"
                options={BOOLEAN_OPTIONS}
                onChange={(val) => setFieldValue('newConstructionYN', val)}
                className="col-span-2"
                name="newConstructionYN"
                disabled={!isEdit}
                value={values.newConstructionYN}
              />
            )}
            <Dropdown
              label="Ownership"
              options={OWNERSHIP_OPTIONS}
              onChange={(val) => setFieldValue('ownership', val)}
              className="col-span-2"
              disabled={!isEdit}
              name="ownership"
              value={values.ownership}
            />
            <Dropdown
              label="Omit if no List Agent Email"
              options={BOOLEAN_OPTIONS}
              onChange={(val) => setFieldValue('listAgentEmailOmitIfNotExists', val)}
              name="listAgentEmailOmitIfNotExists"
              className="col-span-2"
              disabled={!isEdit}
              value={values.listAgentEmailOmitIfNotExists}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default CampaignForm;
